/**
 * SplitDeliveryCard - Confirmed shipping address with optional split delivery
 *
 * Replaces the legacy ShippingAddressCard. Customer can choose:
 *   • Single delivery — one destination (backwards-compatible legacy flow)
 *   • Split delivery — multiple destinations, each with its own quantity
 *
 * Validation rule: sum of split quantities must equal the order quantity.
 * Persists `delivery_mode` and `delivery_addresses[]` on the order via PATCH.
 *
 * Address Book: customer can pick a saved address from a dropdown to pre-fill
 * any destination, and can save a destination back into the address book
 * (via "Save to address book" toggle).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
    Truck, MapPin, Phone, Plus, Trash2, CheckCircle2, AlertTriangle,
    Loader2, Pencil, BookMarked, Save, Package
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import CountrySelect from './CountrySelect';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_DESTINATION = {
    id: null,
    label: '',
    quantity: '',
    company_name: '',
    attention_to: '',
    street: '',
    postal_code: '',
    city: '',
    country: '',
    phone: '',
    delivery_email: '',
    note: '',
    address_book_entry_id: null,
};

function getOrderQuantity(order) {
    if (!order) return 0;
    return (order.vinyl_specs?.quantity) || order.quantity || 0;
}

export default function SplitDeliveryCard({ order, onDeliveryUpdate, canEdit }) {
    const { user, activeCompany } = useAuth();
    const orgId = activeCompany?.id || user?.organization_id;

    const orderQty = getOrderQuantity(order);
    const isSplit = order?.delivery_mode === 'split';
    const hasConfirmed = order?.shipping_confirmed === true;

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [mode, setMode] = useState(isSplit ? 'split' : 'single');
    const [destinations, setDestinations] = useState([]);
    const [addressBook, setAddressBook] = useState([]);
    const [organization, setOrganization] = useState(null);

    // Load address book + organization defaults
    useEffect(() => {
        if (!orgId) return;
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        fetch(`${API_URL}/api/organizations/${orgId}/address-book`, { headers })
            .then(r => r.ok ? r.json() : [])
            .then(setAddressBook)
            .catch(() => {});
        fetch(`${API_URL}/api/organizations/${orgId}`, { headers })
            .then(r => r.ok ? r.json() : null)
            .then(setOrganization)
            .catch(() => {});
    }, [orgId]);

    // Seed form when order changes. We intentionally setState inside useEffect
    // here because the form is editable and we need to reset it whenever the
    // server-side order data refreshes.
    useEffect(() => {
        if (isSplit && order?.delivery_addresses?.length) {
            setMode('split');
            setDestinations(order.delivery_addresses.map(d => ({ ...EMPTY_DESTINATION, ...d })));
        } else if (order?.shipping_address) {
            setMode('single');
            setDestinations([{
                ...EMPTY_DESTINATION,
                ...order.shipping_address,
                quantity: orderQty,
                label: 'Delivery 1',
            }]);
        } else {
            setMode('single');
            setDestinations([{ ...EMPTY_DESTINATION, label: 'Delivery 1', quantity: orderQty }]);
        }
        // eslint-disable-next-line
    }, [order?.shipping_address, order?.delivery_addresses, isSplit, orderQty]);

    const defaultAddress = useMemo(() => {
        const def = addressBook.find(a => a.is_default) || addressBook[0];
        if (def) return def;
        if (!organization) return null;
        if (organization.shipping_address?.street) return organization.shipping_address;
        if (organization.billing_address_line1) {
            return {
                company_name: organization.name,
                attention_to: organization.contact_person || '',
                street: organization.billing_address_line1,
                postal_code: organization.billing_postal_code,
                city: organization.billing_city,
                country: organization.billing_country,
                phone: organization.contact_phone || '',
                delivery_email: organization.contact_email || '',
            };
        }
        return null;
    }, [addressBook, organization]);

    const splitTotal = destinations.reduce((sum, d) => sum + (parseInt(d.quantity, 10) || 0), 0);
    const splitRemaining = orderQty - splitTotal;

    const handleAddDestination = () => {
        setDestinations(prev => [...prev, {
            ...EMPTY_DESTINATION,
            label: `Delivery ${prev.length + 1}`,
            quantity: Math.max(splitRemaining, 0),
        }]);
    };

    const handleRemoveDestination = (idx) => {
        if (destinations.length <= 1) return;
        setDestinations(prev => prev.filter((_, i) => i !== idx).map((d, i) => ({
            ...d,
            label: d.label || `Delivery ${i + 1}`,
        })));
    };

    const handleFieldChange = (idx, field, value) => {
        setDestinations(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
    };

    const handlePickFromBook = (idx, entryId) => {
        const entry = addressBook.find(a => a.id === entryId);
        if (!entry) return;
        setDestinations(prev => prev.map((d, i) => i === idx ? {
            ...d,
            company_name: entry.company_name || '',
            attention_to: entry.attention_to || '',
            street: entry.street,
            postal_code: entry.postal_code,
            city: entry.city,
            country: entry.country,
            phone: entry.phone,
            delivery_email: entry.delivery_email || '',
            address_book_entry_id: entry.id,
            // Keep existing label/quantity/note
        } : d));
    };

    const handleSaveToBook = async (idx) => {
        const d = destinations[idx];
        if (!d.street || !d.postal_code || !d.city || !d.country || !d.phone) {
            toast.error('Fill the address fields first before saving to address book');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/organizations/${orgId}/address-book`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    label: d.label || d.company_name || `${d.city}`,
                    company_name: d.company_name || null,
                    attention_to: d.attention_to || null,
                    street: d.street,
                    postal_code: d.postal_code,
                    city: d.city,
                    country: d.country,
                    phone: d.phone,
                    delivery_email: d.delivery_email || null,
                    note: d.note || null,
                    is_default: false,
                }),
            });
            if (!res.ok) throw new Error('Failed to save');
            const entry = await res.json();
            setAddressBook(prev => [...prev, entry]);
            handleFieldChange(idx, 'address_book_entry_id', entry.id);
            toast.success('Saved to address book');
        } catch {
            toast.error('Could not save to address book');
        }
    };

    const validateDestination = (d, idx) => {
        if (!d.street || !d.postal_code || !d.city || !d.country || !d.phone) {
            return `Destination #${idx + 1}: street, postal code, city, country and phone are required`;
        }
        if (mode === 'split' && (!d.quantity || parseInt(d.quantity, 10) <= 0)) {
            return `Destination #${idx + 1}: quantity must be greater than 0`;
        }
        return null;
    };

    const handleSave = async () => {
        if (mode === 'split') {
            if (!destinations.length) {
                toast.error('Add at least one destination'); return;
            }
            if (orderQty && splitTotal !== orderQty) {
                toast.error(`Split quantities total ${splitTotal} but order quantity is ${orderQty}`); return;
            }
        }
        for (let i = 0; i < destinations.length; i++) {
            const err = validateDestination(destinations[i], i);
            if (err) { toast.error(err); return; }
        }

        setIsSaving(true);
        try {
            if (mode === 'split') {
                await onDeliveryUpdate({
                    delivery_mode: 'split',
                    delivery_addresses: destinations.map((d, i) => ({
                        id: d.id || undefined,
                        label: d.label || `Delivery ${i + 1}`,
                        quantity: parseInt(d.quantity, 10),
                        company_name: d.company_name || null,
                        attention_to: d.attention_to || null,
                        street: d.street,
                        postal_code: d.postal_code,
                        city: d.city,
                        country: d.country,
                        phone: d.phone,
                        delivery_email: d.delivery_email || null,
                        note: d.note || null,
                        address_book_entry_id: d.address_book_entry_id || null,
                    })),
                });
                toast.success('Split delivery confirmed');
            } else {
                const d = destinations[0];
                await onDeliveryUpdate({
                    delivery_mode: 'single',
                    shipping_address: {
                        company_name: d.company_name || null,
                        attention_to: d.attention_to || null,
                        street: d.street,
                        postal_code: d.postal_code,
                        city: d.city,
                        country: d.country,
                        phone: d.phone,
                        delivery_email: d.delivery_email || null,
                    },
                });
                toast.success('Shipping address confirmed');
            }
            setIsEditing(false);
        } catch (e) {
            toast.error(e.message || 'Failed to save delivery');
        } finally {
            setIsSaving(false);
        }
    };

    const handleUseDefault = () => {
        if (!defaultAddress) {
            toast.error('No default address available. Add one in Billing Profile.');
            return;
        }
        setDestinations([{
            ...EMPTY_DESTINATION,
            label: 'Delivery 1',
            quantity: orderQty,
            company_name: defaultAddress.company_name || '',
            attention_to: defaultAddress.attention_to || '',
            street: defaultAddress.street,
            postal_code: defaultAddress.postal_code,
            city: defaultAddress.city,
            country: defaultAddress.country,
            phone: defaultAddress.phone || '',
            delivery_email: defaultAddress.delivery_email || '',
            address_book_entry_id: defaultAddress.id || null,
        }]);
        setMode('single');
        setIsEditing(true);
    };

    const showWarning = !hasConfirmed && canEdit;
    const summaryAddr = isSplit
        ? null
        : (order?.shipping_address || null);
    const summaryDestinations = isSplit ? (order?.delivery_addresses || []) : [];

    return (
        <Card
            className={`mb-6 ${canEdit && !hasConfirmed && !isEditing ? '!border-amber-300 bg-amber-50/40' : ''}`}
            data-testid="delivery-card"
        >
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2 text-slate-700">
                        <Truck className="h-4 w-4" />
                        {isSplit ? 'Delivery Destinations' : 'Shipping Address'}
                        {hasConfirmed && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                        {isSplit && (
                            <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">
                                Split · {summaryDestinations.length}
                            </span>
                        )}
                    </CardTitle>
                    {canEdit && hasConfirmed && !isEditing && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsEditing(true)}
                            className="text-indigo-600 hover:text-indigo-700"
                            data-testid="edit-delivery-btn"
                        >
                            <Pencil className="h-3 w-3 mr-1" />
                            Change
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent>
                {isEditing && canEdit ? (
                    <div className="space-y-4">
                        {/* Mode toggle */}
                        <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-lg w-fit">
                            <button
                                type="button"
                                onClick={() => {
                                    setMode('single');
                                    setDestinations(prev => prev.length ? [{ ...prev[0], quantity: orderQty }] : [{ ...EMPTY_DESTINATION, label: 'Delivery 1', quantity: orderQty }]);
                                }}
                                className={`px-3 py-1 text-xs rounded font-medium transition-colors ${mode === 'single' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'}`}
                                data-testid="mode-single-btn"
                            >
                                Single delivery
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setMode('split');
                                    setDestinations(prev => prev.length >= 1 ? prev : [{ ...EMPTY_DESTINATION, label: 'Delivery 1', quantity: orderQty }]);
                                }}
                                className={`px-3 py-1 text-xs rounded font-medium transition-colors ${mode === 'split' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'}`}
                                data-testid="mode-split-btn"
                            >
                                Split delivery
                            </button>
                        </div>

                        {mode === 'split' && (
                            <div className={`flex items-center gap-2 p-2 rounded-md text-xs border ${splitRemaining === 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : splitRemaining < 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                                <Package className="h-3.5 w-3.5" />
                                <span data-testid="split-summary">
                                    {splitTotal} / {orderQty} units allocated
                                    {splitRemaining !== 0 && (
                                        <span className="ml-2">
                                            ({splitRemaining > 0 ? `${splitRemaining} remaining` : `${Math.abs(splitRemaining)} over`})
                                        </span>
                                    )}
                                </span>
                            </div>
                        )}

                        {/* Destination forms */}
                        {destinations.map((d, idx) => (
                            <DestinationForm
                                key={idx}
                                idx={idx}
                                destination={d}
                                showQuantity={mode === 'split'}
                                addressBook={addressBook}
                                canRemove={mode === 'split' && destinations.length > 1}
                                onChange={(field, value) => handleFieldChange(idx, field, value)}
                                onPickFromBook={(id) => handlePickFromBook(idx, id)}
                                onSaveToBook={() => handleSaveToBook(idx)}
                                onRemove={() => handleRemoveDestination(idx)}
                            />
                        ))}

                        {mode === 'split' && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleAddDestination}
                                data-testid="add-destination-btn"
                            >
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                Add destination
                            </Button>
                        )}

                        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                            <Button
                                size="sm"
                                onClick={handleSave}
                                disabled={isSaving}
                                data-testid="delivery-save-btn"
                            >
                                {isSaving && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                                Confirm Delivery
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : hasConfirmed ? (
                    <div className="text-sm space-y-3">
                        {isSplit ? (
                            summaryDestinations.map((d, i) => (
                                <div key={d.id || i} className="flex items-start gap-2 pb-3 border-b border-slate-100 last:border-b-0 last:pb-0">
                                    <MapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <p className="font-medium text-slate-900">
                                                {d.label || `Delivery ${i + 1}`}
                                            </p>
                                            <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-medium">
                                                Qty: {d.quantity}
                                            </span>
                                        </div>
                                        {d.company_name && <p className="text-slate-700">{d.company_name}</p>}
                                        {d.attention_to && <p className="text-slate-600 text-xs">Attn: {d.attention_to}</p>}
                                        <p className="text-slate-600 text-xs">{d.street}</p>
                                        <p className="text-slate-600 text-xs">{d.postal_code} {d.city}, {d.country}</p>
                                        {d.phone && <p className="flex items-center gap-1 mt-0.5 text-slate-500 text-xs">
                                            <Phone className="h-3 w-3" />{d.phone}
                                        </p>}
                                        {d.note && <p className="mt-1 text-xs italic text-slate-500">&ldquo;{d.note}&rdquo;</p>}
                                    </div>
                                </div>
                            ))
                        ) : summaryAddr ? (
                            <div className="flex items-start gap-2">
                                <MapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    {summaryAddr.company_name && <p className="font-medium">{summaryAddr.company_name}</p>}
                                    {summaryAddr.attention_to && <p className="text-slate-600">Attn: {summaryAddr.attention_to}</p>}
                                    <p>{summaryAddr.street}</p>
                                    <p>{summaryAddr.postal_code} {summaryAddr.city}</p>
                                    <p>{summaryAddr.country}</p>
                                    {summaryAddr.phone && <p className="flex items-center gap-1 mt-1 text-slate-600">
                                        <Phone className="h-3 w-3" />{summaryAddr.phone}
                                    </p>}
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : !canEdit ? (
                    <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-500">
                        <MapPin className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
                        <span>No delivery address on record for this order.</span>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-amber-800">Delivery address required</p>
                                <p className="text-xs text-amber-700 mt-0.5">
                                    Confirm where this order should be delivered. Choose a single address or split between multiple destinations.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleUseDefault}
                                disabled={!defaultAddress}
                                data-testid="use-default-address-btn"
                            >
                                Use default address
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setIsEditing(true)}
                                data-testid="enter-delivery-btn"
                            >
                                Enter delivery details
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function DestinationForm({
    idx, destination, showQuantity, addressBook, canRemove,
    onChange, onPickFromBook, onSaveToBook, onRemove,
}) {
    const d = destination;
    return (
        <div className="rounded-lg border border-slate-200 p-3 space-y-3 bg-white" data-testid={`destination-form-${idx}`}>
            <div className="flex items-center justify-between">
                <Input
                    value={d.label}
                    onChange={(e) => onChange('label', e.target.value)}
                    placeholder={`Delivery ${idx + 1}`}
                    className="h-7 text-xs font-semibold max-w-[180px]"
                    data-testid={`destination-label-${idx}`}
                />
                <div className="flex items-center gap-2">
                    {addressBook.length > 0 && (
                        <select
                            value={d.address_book_entry_id || ''}
                            onChange={(e) => e.target.value && onPickFromBook(e.target.value)}
                            className="h-7 text-xs border rounded-md px-1.5"
                            data-testid={`pick-from-book-${idx}`}
                        >
                            <option value="">Pick from book…</option>
                            {addressBook.map(a => (
                                <option key={a.id} value={a.id}>
                                    {a.label}{a.is_default ? ' (default)' : ''}
                                </option>
                            ))}
                        </select>
                    )}
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={onSaveToBook}
                        className="h-7 px-2 text-xs"
                        title="Save this address to your address book"
                        data-testid={`save-to-book-${idx}`}
                    >
                        <BookMarked className="h-3 w-3 mr-1" /> Save
                    </Button>
                    {canRemove && (
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={onRemove}
                            className="h-7 px-2 text-red-600"
                            data-testid={`remove-destination-${idx}`}
                        >
                            <Trash2 className="h-3 w-3" />
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
                <Field label="Company / Attn" value={d.company_name} onChange={v => onChange('company_name', v)} testid={`d${idx}-company`} />
                <Field label="Attention To" value={d.attention_to} onChange={v => onChange('attention_to', v)} testid={`d${idx}-attention`} />
            </div>
            <Field required label="Street Address" value={d.street} onChange={v => onChange('street', v)} testid={`d${idx}-street`} />
            <div className="grid grid-cols-3 gap-2">
                <Field required label="Postal Code" value={d.postal_code} onChange={v => onChange('postal_code', v)} testid={`d${idx}-postal`} />
                <Field required label="City" value={d.city} onChange={v => onChange('city', v)} testid={`d${idx}-city`} />
                <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Country *</Label>
                    <CountrySelect value={d.country} onChange={(code) => onChange('country', code)} testid={`d${idx}-country`} className="h-8 text-sm" />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <Field required label="Phone" value={d.phone} onChange={v => onChange('phone', v)} testid={`d${idx}-phone`} />
                <Field label="Delivery Email" value={d.delivery_email} onChange={v => onChange('delivery_email', v)} testid={`d${idx}-email`} />
            </div>
            {showQuantity && (
                <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Quantity *</Label>
                        <Input
                            type="number"
                            min="1"
                            value={d.quantity}
                            onChange={(e) => onChange('quantity', e.target.value)}
                            className="h-8 text-sm"
                            data-testid={`d${idx}-qty`}
                        />
                    </div>
                    <div className="space-y-1 col-span-2">
                        <Label className="text-xs text-slate-500">Note / Instructions</Label>
                        <Input
                            value={d.note}
                            onChange={(e) => onChange('note', e.target.value)}
                            placeholder="Optional"
                            className="h-8 text-sm"
                            data-testid={`d${idx}-note`}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, value, onChange, required, testid }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs text-slate-500">
                {label}{required ? ' *' : ''}
            </Label>
            <Input
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                className="h-8 text-sm"
                data-testid={testid}
            />
        </div>
    );
}
